/**
 * Datasource remoto AppContent: abstração + implementação Firestore.
 * Coleção `AppContent/{key}` + subcoleção `versions`.
 * Recebe Firestore por injeção; se omitido, usa getFirestoreDb().
 */

import type { Firestore } from 'firebase/firestore';
import { getDoc, getDocs, orderBy, query, setDoc } from 'firebase/firestore';
import {
  mapFirestoreTimestampsToDates,
  stripUndefinedForFirestore,
} from '@/core/data/firestore-utils';
import { getFirestoreDb } from '@/core/services';
import {
  AppContentModel,
  type AppContentCurrentEntity,
  type AppContentKey,
  type AppContentVersionEntity,
} from '../../domain/entities/app-content.entity';

export interface AppContentRemoteDataSource {
  getCurrent(key: AppContentKey): Promise<AppContentCurrentEntity | null>;
  getVersions(key: AppContentKey): Promise<AppContentVersionEntity[]>;
  setCurrentVersion(key: AppContentKey, entity: AppContentCurrentEntity): Promise<void>;
  addOldVersion(key: AppContentKey, entity: AppContentVersionEntity): Promise<void>;
}

export class AppContentFirestoreDataSource implements AppContentRemoteDataSource {
  constructor(private readonly db?: Firestore) {}

  private getDb(): Firestore {
    return this.db ?? getFirestoreDb();
  }

  async getCurrent(key: AppContentKey): Promise<AppContentCurrentEntity | null> {
    const docRef = AppContentModel.documentRef(this.getDb(), key);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;
    const raw = snapshot.data();
    const mapped = mapFirestoreTimestampsToDates(raw);
    try {
      return AppContentModel.fromCurrentJson(mapped);
    } catch {
      return null;
    }
  }

  async getVersions(key: AppContentKey): Promise<AppContentVersionEntity[]> {
    const colRef = AppContentModel.versionsCollectionRef(this.getDb(), key);
    const q = query(colRef, orderBy('version', 'desc'));
    const snapshot = await getDocs(q);
    const versions: AppContentVersionEntity[] = [];
    snapshot.forEach((docSnap) => {
      const raw = docSnap.data();
      const mapped = mapFirestoreTimestampsToDates(raw);
      try {
        versions.push(AppContentModel.fromVersionJson(mapped));
      } catch {
        /* ignora documento inválido */
      }
    });
    return versions;
  }

  async setCurrentVersion(
    key: AppContentKey,
    entity: AppContentCurrentEntity
  ): Promise<void> {
    const currentDocRef = AppContentModel.documentRef(this.getDb(), key);
    const currentPayload = stripUndefinedForFirestore(
      AppContentModel.toCurrentJson(entity) as Record<string, unknown>
    );
    await setDoc(currentDocRef, currentPayload, { merge: true });
  }

  async addOldVersion(
    key: AppContentKey,
    entity: AppContentVersionEntity
  ): Promise<void> {
    const versionPayload = stripUndefinedForFirestore(
      AppContentModel.toVersionJson(entity) as Record<string, unknown>
    );
    const versionId = `v${entity.version}`;
    const versionDocRef = AppContentModel.versionDocumentRef(
      this.getDb(),
      key,
      versionId
    );
    await setDoc(versionDocRef, versionPayload, { merge: false });
  }
}
